import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, withOptionalCompanyId } from '@/shared/api/base';
import type { Maybe } from '@/shared/lib';

export const REPORT_KEYS = {
  root: () => ['reports'] as const,
  conversations: (conversationId: string, companyId?: string | null) => [
    ...REPORT_KEYS.root(),
    'conversations',
    conversationId,
    companyId ?? null,
  ],
  items: (reportId: string, companyId?: string | null) => [
    ...REPORT_KEYS.root(),
    'items',
    reportId,
    companyId ?? null,
  ],
  items_graph: (conversationId?: string, companyId?: string | null) => [
    ...REPORT_KEYS.root(),
    'items',
    conversationId,
    'graph',
    companyId ?? null,
  ],
};
const keys = REPORT_KEYS;

export const useGetConversationIdReports = (
  conversationId: string,
  companyId?: string | null,
) => {
  const { data, isError, isFetching, isPending, isSuccess } = useQuery({
    queryKey: keys.conversations(conversationId, companyId),
    queryFn: () =>
      api.conversations.listReportsConversationsConversationIdReportsGet(
        conversationId,
        withOptionalCompanyId(companyId),
      ),
  });

  return {
    data: data?.data,
    isError,
    isFetching,
    isPending,
    isSuccess,
  };
};

export const useGetReportItems = (reportId: string, companyId?: string | null) => {
  const { data, isError, isFetching, isPending, isSuccess, status } = useQuery({
    queryKey: keys.items(reportId, companyId),
    queryFn: () =>
      api.reports.listItemsReportsReportIdItemsGet(reportId, withOptionalCompanyId(companyId)),
  });

  return {
    data: data?.data,
    isError,
    isFetching,
    isPending,
    isSuccess,
    status: status,
  };
};

export const useGetReportItemsGraph = (
  conversationId: Maybe<string>,
  companyId?: string | null,
) => {
  const { data, isError, isFetching, isPending, isSuccess, status } = useQuery({
    queryKey: keys.items_graph(conversationId, companyId),
    enabled: !!conversationId,
    retry: false,
    queryFn: () =>
      api.conversations.getProtocolGraphConversationsConversationIdProtocolGraphGet(
        String(conversationId),
        withOptionalCompanyId(companyId),
      ),
  });

  return {
    data: data?.data,
    isError,
    isFetching,
    isPending,
    isSuccess,
    status: status,
  };
};

export const useDeleteReportMutation = (
  id: string,
  conversationId: string,
  onStep: () => void,
  companyId?: string | null,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.reports.deleteReportReportsReportIdDelete(id, withOptionalCompanyId(companyId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.items(id, companyId) });
      queryClient.invalidateQueries({ queryKey: keys.conversations(conversationId, companyId) });
      onStep();
    },
  });
};

export const useProtocolGenerateMutation = (
  conversationId: string,
  companyId?: string | null,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.conversations.triggerProtocolGenerateConversationsConversationIdProtocolGeneratePost(
        conversationId,
        withOptionalCompanyId(companyId),
      ),
    onSuccess: () => {
      // Инвалидируем граф, чтобы начать поллинг или обновить данные
      queryClient.invalidateQueries({ queryKey: keys.items_graph(conversationId, companyId) });
    },
  });
};
